import React, { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, Building2, Users, LogOut,
  ChevronLeft, ChevronRight, Shield,
  Settings, RefreshCw, Ticket, Eye, Server
} from "lucide-react";

const NAV_ITEMS = [
  { to: "/superadmin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/superadmin/organizations", icon: Building2, label: "Organizaciones" },
  { to: "/superadmin/users", icon: Users, label: "Usuarios" },
  { to: "/superadmin/coupons", icon: Ticket, label: "Cupones" },
  { to: "/superadmin/system-status", icon: Server, label: "Estado del sistema" },
];

export default function SuperAdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  // Redirect if not superadmin
  if (user && user.role !== "superadmin") {
    navigate("/dashboard", { replace: true });
    return null;
  }

  return (
    <div className="flex h-screen bg-slate-950" data-testid="superadmin-layout">
      {/* Sidebar */}
      <aside
        className={`${collapsed ? "w-16" : "w-64"} bg-gradient-to-b from-slate-900 to-slate-950 border-r border-amber-500/20 flex flex-col transition-all duration-200`}
      >
        {/* Header */}
        <div className={`p-4 border-b border-amber-500/20 ${collapsed ? "px-2" : ""}`}>
          <div className={`flex items-center ${collapsed ? "justify-center" : "justify-between"}`}>
            {!collapsed && (
              <div className="flex items-center gap-2">
                <div className="p-2 bg-amber-500/10 rounded-lg">
                  <Shield className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <span className="text-amber-400 font-bold text-lg">SuperAdmin</span>
                  <p className="text-xs text-slate-500">Notalia Platform</p>
                </div>
              </div>
            )}
            {collapsed && (
              <div className="p-2 bg-amber-500/10 rounded-lg">
                <Shield className="w-5 h-5 text-amber-400" />
              </div>
            )}
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="text-slate-500 hover:text-slate-300 hidden lg:block"
            >
              {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 space-y-1 px-2">
          {NAV_ITEMS.map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                    : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"
                } ${collapsed ? "justify-center" : ""}`
              }
            >
              <Icon size={18} />
              {!collapsed && <span>{label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* User Info */}
        <div className="p-3 border-t border-amber-500/20">
          {!collapsed && user && (
            <div className="mb-3 px-2">
              <p className="text-sm font-medium text-amber-300 truncate">{user.name}</p>
              <p className="text-xs text-slate-500 truncate">{user.email}</p>
              <div className="flex items-center gap-1 mt-1">
                <Shield size={10} className="text-amber-500" />
                <span className="text-xs text-amber-500 font-medium">SuperAdmin</span>
              </div>
            </div>
          )}
          <button
            onClick={() => {
              try { sessionStorage.setItem("notalia_admin_viewing_app", "true"); } catch { /* private mode */ }
              navigate("/dashboard");
            }}
            className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-teal-400 hover:text-teal-300 hover:bg-teal-500/10 transition-colors mb-1 ${collapsed ? "justify-center" : ""}`}
            title="Ver la app como usuario normal"
          >
            <Eye size={18} />
            {!collapsed && <span>Ver como usuario</span>}
          </button>
          <button
            onClick={handleLogout}
            className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors ${collapsed ? "justify-center" : ""}`}
          >
            <LogOut size={18} />
            {!collapsed && <span>Cerrar Sesión</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto bg-slate-950">
        <div className="p-6 lg:p-8">
          {children || <Outlet />}
        </div>
      </main>
    </div>
  );
}
